import { useCallback, useSyncExternalStore } from 'react'
import { debugToolsEnabled } from '@/ett-debug-tools/debug-enabled'
import { posthog, isPostHogEnabled } from './posthog'
import { readDevFlagOverride } from './feature-flags'

/**
 * A rollout flag has three honest answers, and callers that gate a route
 * need all three: 'loading' must render nothing rather than redirect, or a
 * legitimate user gets bounced off their own URL on first paint.
 *
 * - PostHog not configured (local dev): 'on'. Dev sees every surface.
 * - Configured, flags not yet delivered: 'loading'. PostHog caches flags
 *   in localStorage, so this is a first-visit gap, not a per-load one.
 * - Delivered: the flag's value; a flag missing from the project is 'off'.
 */
export type FlagState = 'on' | 'off' | 'loading'

interface FlagOptions {
  /**
   * Whether the `ett:flags` override is honoured. Test seam; production callers
   * leave it to `debugToolsEnabled` (dev, and staging with debug tools), the
   * same gate `readDevFlagOverride` documents. Defaulting it to
   * `import.meta.env.DEV` here made the override inert on staging.
   */
  isDev?: boolean
}

/** One read of the three answers; the hook and the synchronous helper share it. */
function readFlagState(flag: string, overrideEnabled: boolean): FlagState {
  const override = readDevFlagOverride(flag, undefined, overrideEnabled)
  if (override != null) return override ? 'on' : 'off'
  if (!isPostHogEnabled()) return 'on'
  // undefined until the flags request lands; boolean after. send_event:
  // false: read on every render, and each $feature_flag_called is billed.
  const val = posthog.isFeatureEnabled(flag, { send_event: false })
  if (val === undefined) return 'loading'
  return val ? 'on' : 'off'
}

export function useFeatureFlagState(flag: string, opts: FlagOptions = {}): FlagState {
  const isDev = opts.isDev ?? debugToolsEnabled

  const subscribe = useCallback((cb: () => void) => {
    if (!isPostHogEnabled()) return () => {}
    // onFeatureFlags returns its own unsubscribe; dropping it leaked a
    // handler per mount.
    return posthog.onFeatureFlags(cb)
  }, [])

  const getSnapshot = useCallback((): FlagState => readFlagState(flag, isDev), [flag, isDev])

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot)
}

/** Boolean convenience: 'loading' reads as off, so a surface hides until ready. */
export function useFeatureFlag(flag: string, opts: FlagOptions = {}): boolean {
  return useFeatureFlagState(flag, opts) === 'on'
}

/**
 * `useFeatureFlag` for code that cannot call a hook (request builders, module-level
 * registries): the same rules (override first, PostHog not configured is on, not yet
 * delivered is off), read once. It does not subscribe, so read it at the moment of
 * use, never at module load, when PostHog has not answered yet.
 */
export function isFeatureFlagOn(flag: string, opts: FlagOptions = {}): boolean {
  return readFlagState(flag, opts.isDev ?? debugToolsEnabled) === 'on'
}
